import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, PhoneCall, Sparkles, Star } from "lucide-react";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/cf4fc7f2-a73a-4fe6-9ce3-8070ac060fc8/images/683255038baa1423dfaa76f08b3920d7ac2389d2fdf580f6fd209ae2a302ddff.png";

export const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden border-b-2 border-ink"
    >
      {/* confetti background */}
      <div className="absolute inset-0 opacity-[0.18] confetti-bg pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-16 pb-24 lg:pt-24 lg:pb-32 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border-2 border-ink rounded-full text-sm font-bold shadow-brutal tilt-left"
            data-testid="hero-eyebrow"
          >
            <Sparkles size={14} className="text-[var(--pink)]" />
            AI agency for UK solo entrepreneurs
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="font-display mt-6 text-5xl sm:text-6xl lg:text-7xl font-black leading-[0.95] tracking-tight"
            data-testid="hero-heading"
          >
            Win back your
            <br />
            <span className="doodle-underline">evenings.</span>{" "}
            <span className="text-[var(--pink)]">Book more</span>
            <br />
            <span className="text-[var(--blue)]">jobs</span> while you sleep.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="mt-6 text-lg sm:text-xl text-stone-700 max-w-2xl leading-relaxed"
            data-testid="hero-subheading"
          >
            We build practical AI assistants and workflow automations for
            plumbers, electricians, gas engineers, mobile therapists, cleaners
            and other busy solo traders. No jargon. Plain English. Real time
            saved every week.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.25 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <a
              href="#book"
              data-testid="hero-cta-primary"
              className="btn-ink inline-flex items-center gap-2 bg-[var(--pink)] text-white px-6 py-3.5 text-base"
            >
              Book a free consultation <ArrowRight size={18} />
            </a>
            <a
              href="#services"
              data-testid="hero-cta-secondary"
              className="btn-ink inline-flex items-center gap-2 bg-white text-ink px-6 py-3.5 text-base"
            >
              <PhoneCall size={18} /> See what we build
            </a>
          </motion.div>

          <div className="mt-10 flex flex-wrap items-center gap-6 text-sm text-stone-700">
            <div
              className="flex items-center gap-1.5"
              data-testid="hero-rating"
            >
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  size={16}
                  className="fill-[var(--yellow)] text-[var(--yellow)]"
                />
              ))}
              <span className="ml-1 font-bold">5.0</span>
              <span>from early customers</span>
            </div>
            <div className="font-semibold">
              <span className="text-[var(--blue)]">£0</span> setup fee on starter
              packages
            </div>
          </div>
        </div>

        {/* Right visual */}
        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.96, rotate: -2 }}
            animate={{ opacity: 1, scale: 1, rotate: -2 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="relative bg-white border-2 border-ink rounded-[28px] shadow-brutal-lg overflow-hidden"
            data-testid="hero-image-wrap"
          >
            <img
              src={HERO_IMG}
              alt="Playful 3D abstract shapes representing AI automation"
              className="w-full h-[420px] object-cover"
            />
          </motion.div>

          {/* Floating sticker cards */}
          <motion.div
            initial={{ opacity: 0, y: 20, rotate: 6 }}
            animate={{ opacity: 1, y: 0, rotate: 6 }}
            transition={{ duration: 0.5, delay: 0.45 }}
            className="absolute -bottom-6 -left-6 bg-[var(--yellow)] border-2 border-ink rounded-2xl px-4 py-3 shadow-brutal max-w-[220px]"
            data-testid="hero-sticker-time"
          >
            <p className="text-xs font-bold uppercase tracking-widest text-stone-800">
              Avg. result
            </p>
            <p className="font-display text-2xl font-black leading-tight">
              +12 hours
              <br />
              saved / week
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: -20, rotate: -4 }}
            animate={{ opacity: 1, y: 0, rotate: -4 }}
            transition={{ duration: 0.5, delay: 0.55 }}
            className="absolute -top-4 -right-4 bg-[var(--blue)] text-white border-2 border-ink rounded-2xl px-4 py-3 shadow-brutal"
            data-testid="hero-sticker-calls"
          >
            <p className="text-xs font-bold uppercase tracking-widest">
              Never miss
            </p>
            <p className="font-display text-2xl font-black leading-tight">
              a job again
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
